import { EMPTY_STRING, Nullable } from '@rotomeca/utils';
import BnumElementInternal from '../../bnum-element-states';
import { Scheduler } from '../../../core/utils/Scheduler';
import style from './bnum-badge.less';
import { CustomDom } from '../../bnum-element';
import { Define } from '../../../core/decorators';
import { UpdateAll } from 'core/decorators/updateAll';
import { TAG_BADGE } from 'core/utils/constants/tags';

//#region Types
export type BnumBadgeVariation = 'primary' | 'secondary' | 'danger';
//#endregion Types

//#region Global constants
const DATA_VALUE = 'value';
const DATA_VARIATION = 'variation';

const ATTR_VALUE = 'data-value';
const ATTR_VARIATION = 'data-variation';
const ATTR_CIRCLE = 'circle';

const VARIATION_PRIMARY = 'primary';
// Not used currently
// const VARIATION_SECONDARY = 'secondary';
// const VARIATION_DANGER = 'danger';

const STATE_HAS_VALUE = 'has-value';
const STATE_NO_VALUE = 'no-value';
const STATE_IS_CIRCLE = 'is-circle';
const STATE_VARIATION_PREFIX = 'variation-';
//#endregion Global constants

/**
 * Badge d'information.
 *
 * @structure Badge classique
 * <bnum-badge data-value="Je suis un badge !"></bnum-badge>
 *
 * @structure Badge avec un nombre
 * <bnum-badge data-value="9999"></bnum-badge>
 *
 * @structure Arrondi forcé
 * <bnum-badge data-value="9999" circle></bnum-badge>
 *
 * @structure Secondary
 * <bnum-badge data-value="42" data-variation="secondary" circle></bnum-badge>
 *
 * @structure Danger
 * <bnum-badge data-value="42" data-variation="danger" circle></bnum-badge>
 *
 * @state has-value - Le badge a une valeur.
 * @state no-value - Le badge n'a pas de valeur.
 * @state is-circle - Le badge est en mode cercle.
 * @state variation-primary - Le badge utilise la variation primaire.
 * @state variation-secondary - Le badge utilise la variation secondaire.
 * @state variation-danger - Le badge utilise la variation danger.
 *
 * @attr {string} data-value - Valeur affichée dans le badge.
 * @attr {'primary' | 'secondary' | 'danger'} (optional) (default:'primary') data-variation - Variation du badge.
 * @attr {any} (optional) circle - Indique si le badge doit être affiché en cercle.
 *
 * @cssvar {inline-block} --bnum-badge-display - Permet de surcharger la propriété CSS display du badge.
 * @cssvar {100px} --bnum-badge-border-radius - Permet de surcharger le rayon de bordure du badge.
 * @cssvar {10px} --bnum-badge-padding - Permet de surcharger le padding du badge.
 * @cssvar {100%} --bnum-badge-circle-border-radius - Permet de surcharger le rayon de bordure du badge en mode "cercle".
 * @cssvar {#000091} --bnum-badge-primary-color - Définit la couleur de fond du badge en variation "primary".
 * @cssvar {#f5f5fe} --bnum-badge-primary-text-color - Définit la couleur du texte du badge en variation "primary".
 * @cssvar {#ffffff} --bnum-badge-secondary-color - Définit la couleur de fond du badge en variation "secondary".
 * @cssvar {#000091} --bnum-badge-secondary-text-color - Définit la couleur du texte du badge en variation "secondary".
 * @cssvar {solid} --bnum-badge-type - Permet de surcharger le type de bordure (ex: solid, dashed) pour la variation "secondary".
 * @cssvar {thin} --bnum-badge-size - Permet de surcharger l’épaisseur de la bordure pour la variation "secondary".
 * @cssvar {#ce0500} --bnum-badge-danger-color - Définit la couleur de fond du badge en variation "danger".
 * @cssvar {#f5f5fe} --bnum-badge-danger-text-color - Définit la couleur du texte du badge en variation "danger".
 *
 */
@Define({
	styles: style,
	tag: TAG_BADGE,
})
@UpdateAll()
export class HTMLBnumBadge extends BnumElementInternal {
	//#region Private Fields
	/**
	 * Valeur affichée dans le badge.
	 */
	#_value: string = EMPTY_STRING;
	/**
	 * Planificateur de mise à jour asynchrone.
	 */
	#_updateSchduler: Nullable<Scheduler<number>> = null;
	/**
	 * Élément span contenant la valeur du badge.
	 */
	#_spanElement: Nullable<HTMLSpanElement> = null;
	//#endregion Private Fields

	//#region Getters/Setters
	/**
	 * Récupère la valeur depuis l'attribut data-value.
	 */
	get #_dataValue(): string {
		return this.data(DATA_VALUE) || EMPTY_STRING;
	}

	/**
	 * Récupère la variation depuis l'attribut data-variation.
	 */
	get #_dataVariation(): string {
		return this.data(DATA_VARIATION) || VARIATION_PRIMARY;
	}
	/**
	 * Valeur affichée dans le badge.
	 */
	get value(): string {
		if (!this.alreadyLoaded) this.#_value = this.#_dataValue;
		return this.#_value;
	}

	set value(value: string) {
		if (!this.alreadyLoaded) this.removeAttribute(ATTR_VALUE);
		this.#_value = value;

		this.#_requestUpdate();
	}

	/**
	 * Variation de style du badge.
	 */
	get variation(): string {
		return this.#_dataVariation;
	}

	set variation(value: string) {
		this.data(DATA_VARIATION, value);

		this.#_requestUpdate();
	}
	//#endregion Getters/Setters

	//#region Lifecycle
	constructor() {
		super();
	}

	/**
	 * Construit le DOM interne du composant.
	 */
	protected _p_buildDOM(container: CustomDom): void {
		super._p_buildDOM(container);

		this.#_spanElement = this._p_createSpan();
		container.appendChild(this.#_spanElement);

		const force = true;
		this.#_update(force);
	}

	/**
	 * Met à jour le composant lors d'un changement d'attribut.
	 */
	protected _p_update(): void | Nullable<'break'> {
		return this.#_update();
	}
	//#endregion Lifecycle

	//#region Private Methods

	/**
	 * Demande une mise à jour asynchrone du composant.
	 */
	#_requestUpdate(): this {
		if (!this.#_updateSchduler) {
			this.#_updateSchduler = new Scheduler<number>(() => this.#_update());
			this._p_registerDisposable(this.#_updateSchduler);
		}
		this.#_updateSchduler.schedule(0);
		return this;
	}

	/**
	 * Met à jour l'affichage du badge selon ses propriétés et attributs.
	 */
	#_update(force: boolean = false): void {
		if (!this.alreadyLoaded && !force) return;

		this._p_clearStates();

		const value = this.value;
		this.#_spanElement!.textContent = value;

		if (value !== EMPTY_STRING) this._p_addState(STATE_HAS_VALUE);
		else this._p_addState(STATE_NO_VALUE);

		if (this.hasAttribute(ATTR_CIRCLE)) this._p_addState(STATE_IS_CIRCLE);
		this._p_addState(`${STATE_VARIATION_PREFIX}${this.variation}`);
	}
	//#endregion Private Methods

	forceValue(value: string) {
		this.#_value = value;
		this.#_update(true);
	}

	//#region Static Methods
	/**
	 * Attributs observés pour ce composant.
	 */
	protected static _p_observedAttributes(): string[] {
		return [ATTR_CIRCLE];
	}

	/**
	 * Crée un badge via JavaScript.
	 * @param value Valeur à afficher
	 * @param options Options de création (cercle, variation)
	 */
	static Create(
		value: string,
		{
			circle = false,
			variation = undefined,
		}: {
			circle?: boolean;
			variation?: BnumBadgeVariation;
		} = {},
	): HTMLBnumBadge {
		const badge = document.createElement(this.TAG) as HTMLBnumBadge;

		return badge
			.attr(ATTR_VALUE, value)
			.condAttr(circle, ATTR_CIRCLE, true)
			.condAttr(variation !== undefined, ATTR_VARIATION, variation!);
	}

	/**
	 * Génère le HTML d'un badge.
	 * @param value Valeur à afficher
	 * @param attrs Attributs additionnels
	 */
	static Write(value: string, attrs: Record<string, string> = {}): string {
		const attributes = this._p_WriteAttributes(attrs);
		return `<${this.TAG} ${ATTR_VALUE}="${value}" ${attributes}></${this.TAG}>`;
	}
	//#endregion Static Methods
}
